import { z } from 'zod';
/**
 * ISO 8601 timestamp string. Firestore Timestamps serialize to this at the API boundary.
 */
export const IsoTimestamp = z.string().datetime({ offset: true });
/**
 * Firestore document ID. Opaque string, 1-1500 chars, no slashes.
 */
export const DocId = z
    .string()
    .min(1)
    .max(1500)
    .regex(/^[^/]+$/);
/**
 * Tenant ID. Lower-case slug of the tenant organisation.
 * `suppohrt` is the primary tenant. Each zorggroep gets its own (e.g. `rhogo`).
 *
 * Reserved special form: `_platform` — used by audit_log + system_status
 * writes that are not bound to a real tenant (cross-tenant admin actions
 * on shared collections like kennisbank_ring1). The leading underscore
 * is intentional and reserved; no real tenant slug may start with one.
 */
export const TenantId = z
    .string()
    .min(2)
    .max(64)
    .regex(/^_?[a-z][a-z0-9-]*$/, 'Tenant ID must be lowercase alphanumeric with hyphens (or `_platform` for cross-tenant system writes)');
/**
 * User role.
 *
 * Hierarchy (most privileged → least):
 *   suppohrt_admin     — platform admin (Linda, Diederik). Cross-tenant.
 *   zorggroep_admin    — external zorggroep admin. Own-tenant only.
 *   suppohrt_medewerker — internal SUPPOHRT staff. Sees internal docs.
 *   zorggroep_gebruiker — external zorggroep practitioner. Clinical + SK only.
 */
export const UserRole = z.enum([
    'suppohrt_admin',
    'suppohrt_medewerker',
    'zorggroep_admin',
    'zorggroep_gebruiker',
]);
/**
 * Content trust ring.
 *
 *   1 — SUPPOHRT eigen bron (highest trust, internal or clinical)
 *   2 — Geverifieerde Nederlandse bron (NHG, Trimbos, GGD, etc.)
 *   3 — Niet-geverifieerd (DISABLED AT LAUNCH per /office-hours decision)
 */
export const ContentRing = z.union([z.literal(1), z.literal(2), z.literal(3)]);
/**
 * Document access level — enforced at retrieval time.
 *
 *   clinical — visible to all authenticated users regardless of tenant.
 *   internal — visible only to suppohrt_admin + suppohrt_medewerker.
 */
export const AccessLevel = z.enum(['clinical', 'internal']);
/**
 * Helper: does a given role have internal-document access?
 * Used by retrieval-time filtering in the agent.
 */
export function roleCanSeeInternal(role) {
    return role === 'suppohrt_admin' || role === 'suppohrt_medewerker';
}
/**
 * Beheerders van de eigen tenant (ADR 0018): suppohrt_admin in SUPPOHRT,
 * zorggroep_admin in een klant-tenant. Eén lijst, zodat backend-poorten,
 * meldingsontvangers en menu niet uit elkaar lopen.
 */
export const TENANT_ADMIN_ROLES = ['suppohrt_admin', 'zorggroep_admin'];
/**
 * Helper: can a given role administer the platform (Beheer)?
 */
export function roleIsAdmin(role) {
    return TENANT_ADMIN_ROLES.includes(role);
}
/**
 * Helper: is a role cross-tenant (can view/manage other tenants)?
 * Only suppohrt_admin qualifies. Zorggroep admins manage their OWN tenant only.
 */
export function roleIsCrossTenant(role) {
    return role === 'suppohrt_admin';
}
/**
 * Standard metadata every Firestore doc carries.
 * Not all docs have updatedBy (first-write only sets createdBy).
 */
export const AuditMeta = z.object({
    createdAt: IsoTimestamp,
    createdBy: DocId,
    updatedAt: IsoTimestamp.optional(),
    updatedBy: DocId.optional(),
});
//# sourceMappingURL=common.js.map